import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = 'https://aaktradex.in';

const C = {
  green: '#3B6D11', lgreen: '#EAF3DE', bgreen: '#C0DD97',
  amber: '#854F0B', lamber: '#FAEEDA', bamber: '#FAC775',
  red: '#A32D2D', lred: '#FCEBEB', bred: '#F7C1C1',
  blue: '#185FA5', lblue: '#E6F1FB', bblue: '#B5D4F4',
  purple: '#6B21A8', lpurple: '#F3E8FF', bpurple: '#C4B5FD',
};

const UNIVERSES = ['Nifty Total Market', 'Nifty 500', 'Nifty 100', 'Nifty 50', 'F&O Universe', 'Trendlyne Universe'];

function StatusPill({ status }) {
  const map = {
    success: { color: C.green, bg: C.lgreen, border: C.bgreen, label: 'success' },
    partial: { color: C.amber, bg: C.lamber, border: C.bamber, label: 'partial' },
    failed: { color: C.red, bg: C.lred, border: C.bred, label: 'failed' },
  };
  const s = map[status] || { color: '#666', bg: '#eee', border: '#ccc', label: status || '-' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 500, padding: '2px 8px',
      borderRadius: 20, background: s.bg, color: s.color,
      border: `0.5px solid ${s.border}`,
    }}>{s.label}</span>
  );
}

function formatDT(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
}

export default function DataUploadPanel() {
  const [categories, setCategories] = useState([]);
  const [lastUploads, setLastUploads] = useState({});
  const [history, setHistory] = useState([]);
  const [universe, setUniverse] = useState('Nifty Total Market');
  const [uploading, setUploading] = useState({});
  const [snapshotTime, setSnapshotTime] = useState('15:30');
  const [message, setMessage] = useState(null);
  const fileInputRefs = useRef({});

  const fetchAll = useCallback(async () => {
    try {
      const [catsRes, lastRes, histRes] = await Promise.all([
        axios.get(`${API}/trendlyne/categories`),
        axios.get(`${API}/trendlyne/last-uploads`),
        axios.get(`${API}/trendlyne/upload-history?limit=20`),
      ]);
      setCategories(catsRes.data.categories || []);
      setLastUploads(lastRes.data.summary || {});
      setHistory(histRes.data.history || []);
    } catch (e) {
      console.error('Failed to fetch panel data', e);
      setMessage({ type: 'error', text: 'Failed to load: ' + (e.response?.data?.detail || e.message) });
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleFilePicked = async (category, event) => {
    const file = event.target.files[0];
    if (!file) return;
    const meta = categories.find(c => c.key === category);
    const isBreadth = meta?.has_time;

    setUploading(prev => ({ ...prev, [category]: true }));
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('universe', universe);
      if (isBreadth) formData.append('snapshot_time', snapshotTime);

      const res = await axios.post(`${API}/trendlyne/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });
      const d = res.data;
      setMessage({
        type: d.status === 'success' ? 'success' : (d.status === 'partial' ? 'warn' : 'error'),
        text: `${meta?.label || category}: ${d.rows_upserted}/${d.rows_read} rows for ${d.snapshot_date} (${d.status})`,
      });
      await fetchAll();
    } catch (e) {
      setMessage({
        type: 'error',
        text: `Upload failed: ${e.response?.data?.detail || e.message}`,
      });
    } finally {
      setUploading(prev => ({ ...prev, [category]: false }));
      if (fileInputRefs.current[category]) fileInputRefs.current[category].value = '';
    }
  };

  const tabularCats = categories.filter(c => !c.has_time);
  const breadthCats = categories.filter(c => c.has_time);

  const btnStyle = (isUp) => ({
    fontSize: 10, padding: '4px 12px', borderRadius: 6,
    border: `0.5px solid ${C.bblue}`, background: isUp ? '#eee' : C.lblue,
    color: C.blue, cursor: isUp ? 'wait' : 'pointer', fontWeight: 500,
  });

  return (
    <div style={{ padding: 16, maxWidth: 1100 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Trendlyne Data Uploads</h2>
      <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 16 }}>
        Upload weekly/daily Trendlyne exports. Snapshot date auto-detected from filename. History preserved across days.
      </div>

      <div style={{ marginBottom: 16, padding: 12, background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--t2)', marginRight: 8 }}>Universe:</label>
            <select value={universe} onChange={e => setUniverse(e.target.value)}
                    style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)' }}>
              {UNIVERSES.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 10, color: 'var(--t3)' }}>
            Filename convention: 2026-07-23-nifty-total-market_Category.xlsx
          </div>
        </div>
      </div>

      {message && (
        <div style={{
          padding: '10px 12px', borderRadius: 8, marginBottom: 16, fontSize: 12,
          background: message.type === 'success' ? C.lgreen : message.type === 'warn' ? C.lamber : C.lred,
          color: message.type === 'success' ? C.green : message.type === 'warn' ? C.amber : C.red,
          border: `1px solid ${message.type === 'success' ? C.bgreen : message.type === 'warn' ? C.bamber : C.bred}`,
        }}>
          {message.text}
          <button onClick={() => setMessage(null)}
                  style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 14 }}>
            X
          </button>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--t1)' }}>
          Weekly/Daily Trendlyne Data (XLSX)
        </div>
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg2)', color: 'var(--t2)' }}>
              <th style={{ textAlign: 'left', padding: '8px 10px' }}>Category</th>
              <th style={{ textAlign: 'left', padding: '8px 10px' }}>Last Snapshot</th>
              <th style={{ textAlign: 'left', padding: '8px 10px' }}>Last Uploaded</th>
              <th style={{ textAlign: 'right', padding: '8px 10px' }}>Rows</th>
              <th style={{ textAlign: 'left', padding: '8px 10px' }}>Status</th>
              <th style={{ textAlign: 'right', padding: '8px 10px' }}>Upload</th>
            </tr>
          </thead>
          <tbody>
            {tabularCats.map(cat => {
              const last = lastUploads[cat.key] || {};
              const isUp = uploading[cat.key];
              return (
                <tr key={cat.key} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{cat.label}</td>
                  <td style={{ padding: '8px 10px', color: 'var(--t3)' }}>{last.last_snapshot_date || '-'}</td>
                  <td style={{ padding: '8px 10px', color: 'var(--t3)' }}>{formatDT(last.last_uploaded_at)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{last.last_rows ?? '-'}</td>
                  <td style={{ padding: '8px 10px' }}><StatusPill status={last.last_status} /></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <input ref={el => (fileInputRefs.current[cat.key] = el)} type="file"
                           accept={cat.file_type === 'csv' ? '.csv' : '.xlsx'}
                           onChange={e => handleFilePicked(cat.key, e)} disabled={isUp} style={{ display: 'none' }} />
                    <button onClick={() => fileInputRefs.current[cat.key]?.click()} disabled={isUp} style={btnStyle(isUp)}>
                      {isUp ? 'Uploading...' : 'Choose file'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {breadthCats.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--t1)' }}>
            Market Breadth (CSV, multiple snapshots/day allowed)
          </div>
          <div style={{ marginBottom: 10, fontSize: 11 }}>
            <label style={{ color: 'var(--t2)', marginRight: 8 }}>Snapshot Time (IST):</label>
            <input type="time" value={snapshotTime} onChange={e => setSnapshotTime(e.target.value)}
                   style={{ fontSize: 12, padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border)' }} />
            <span style={{ fontSize: 10, color: 'var(--t3)', marginLeft: 8 }}>
              (e.g., 09:30 pre-open, 12:00 midday, 15:30 EOD)
            </span>
          </div>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg2)', color: 'var(--t2)' }}>
                <th style={{ textAlign: 'left', padding: '8px 10px' }}>Granularity</th>
                <th style={{ textAlign: 'left', padding: '8px 10px' }}>Last Snapshot</th>
                <th style={{ textAlign: 'left', padding: '8px 10px' }}>Last Uploaded</th>
                <th style={{ textAlign: 'right', padding: '8px 10px' }}>Rows</th>
                <th style={{ textAlign: 'left', padding: '8px 10px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '8px 10px' }}>Upload</th>
              </tr>
            </thead>
            <tbody>
              {breadthCats.map(cat => {
                const last = lastUploads[cat.key] || {};
                const isUp = uploading[cat.key];
                return (
                  <tr key={cat.key} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 500 }}>{cat.label}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--t3)' }}>{last.last_snapshot_date || '-'}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--t3)' }}>{formatDT(last.last_uploaded_at)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{last.last_rows ?? '-'}</td>
                    <td style={{ padding: '8px 10px' }}><StatusPill status={last.last_status} /></td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      <input ref={el => (fileInputRefs.current[cat.key] = el)} type="file" accept=".csv"
                             onChange={e => handleFilePicked(cat.key, e)} disabled={isUp} style={{ display: 'none' }} />
                      <button onClick={() => fileInputRefs.current[cat.key]?.click()} disabled={isUp} style={btnStyle(isUp)}>
                        {isUp ? 'Uploading...' : 'Choose file'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--t1)' }}>
          Upload History (last 20)
        </div>
        {history.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--t3)', padding: 12, textAlign: 'center' }}>No uploads yet.</div>
        ) : (
          <table style={{ width: '100%', fontSize: 10.5, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg2)', color: 'var(--t2)' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>When</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Category</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Universe</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Filename</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Snapshot</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Rows</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Failed</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '5px 8px', color: 'var(--t3)' }}>{formatDT(h.uploaded_at)}</td>
                  <td style={{ padding: '5px 8px', fontWeight: 500 }}>{h.category}</td>
                  <td style={{ padding: '5px 8px', color: 'var(--t3)' }}>{h.universe || '-'}</td>
                  <td style={{ padding: '5px 8px', color: 'var(--t3)', fontSize: 10 }} title={h.filename}>
                    {h.filename ? (h.filename.length > 40 ? h.filename.substring(0, 40) + '...' : h.filename) : '-'}
                  </td>
                  <td style={{ padding: '5px 8px', color: 'var(--t3)' }}>{h.snapshot_date}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right' }}>{h.rows_upserted}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', color: h.rows_failed > 0 ? C.red : 'var(--t3)' }}>
                    {h.rows_failed}
                  </td>
                  <td style={{ padding: '5px 8px' }}><StatusPill status={h.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
